import axios from "axios";
import { isEqual } from "lodash-es";
import { useCallback, useEffect, useMemo, useState } from "react";

import generalRequestErrorHandler from "../../components/apiHooks/generalRequestErrorHandler";
import { addJWTInConfig } from "../../components/apiHooks/helpers";
import useApiRequestGet from "../../components/apiHooks/useApiRequestGet";
import { useConfigContext } from "../../contexts/ConfigContext/ConfigContext";
import { usePaymentActions, usePaymentContext } from "../../contexts/PaymentContext/PaymentContext";
import { useUrlParams } from "../../router/hooks";
import { IOrderDetail } from "./types";

const HOST: string = process.env.CLOUD_RUN_GATEWAY_HOST ?? "";
const END_POINT: string = process.env.API_ORDER_DETAIL ?? "";

interface UseGetOrderDetailOptions {
	autoFetch?: boolean;
}

const useGetOrderDetail = (options: UseGetOrderDetailOptions = {}) => {
	const { autoFetch = true } = options;
	const { merchantReference: urlPaymentReference } = useUrlParams();
	const { state: providedPaymentReference } = usePaymentContext((state) => state.paymentReference);
	const { SET_ORDER_DETAIL, SET_ORDER_STATUS } = usePaymentActions();
	const paymentReferenceFromHook = urlPaymentReference || providedPaymentReference;
	const [orderDetail, setOrderDetail] = useState<IOrderDetail | null>(null);
	const {
		state: { user },
	} = useConfigContext((state) => state);

	const url = useMemo(() => {
		if (!paymentReferenceFromHook) return null;

		return `${HOST}${END_POINT}?uuid=${paymentReferenceFromHook}`;
	}, [paymentReferenceFromHook]);

	const payload = useMemo(() => {
		// Don't make request if URL is not available
		if (!url) {
			return {
				url: null,
				axiosConfig: undefined,
				triggerOnMount: false,
				swrConfig: {
					revalidateOnFocus: false,
				},
			};
		}

		const authToken = user?.session_id?.toString() ?? "";

		// Allow request even with empty token, but add retry logic for auth errors

		return {
			url,
			triggerOnMount: autoFetch,
			axiosConfig: addJWTInConfig(undefined, authToken),
			swrConfig: {
				revalidateOnFocus: false,
				shouldRetryOnError: (error: any) => {
					// Retry 401 errors (JWT might be initializing) but limit to 2 attempts
					if (error?.response?.status === 401) {
						return true;
					}

					// Only retry on 5xx server errors, not other 4xx client errors
					return error?.response?.status >= 500;
				},
				errorRetryCount: 5,
				errorRetryInterval: 1000, // 1 second delay between retries
			},
		};
	}, [url, user, autoFetch]);

	const {
		data: response,
		isLoading,
		error,
		isValidating,
		getUpdateData,
	} = useApiRequestGet(payload);

	const fetchOrderDetail = useCallback(
		async (refToFetch?: string) => {
			const finalRef = refToFetch || paymentReferenceFromHook;

			if (!finalRef) {
				console.error("fetchOrderDetail called without a payment reference.");

				return null;
			}

			const fetchUrl = `${HOST}${END_POINT}?uuid=${finalRef}`;
			const maxRetries = 10;
			const retryDelay = 1500;

			for (let attempt = 1; attempt <= maxRetries; attempt++) {
				try {
					console.log(`🔍 Fetching order details attempt ${attempt}/${maxRetries}`);

					// Use mutate with the specific URL to avoid conflicts
					const result = await getUpdateData(
						fetcher(fetchUrl, addJWTInConfig(undefined, user?.session_id?.toString() ?? "")),
						{
							revalidate: false,
							throwOnError: true, // Ensure errors are properly thrown
						}
					);

					console.log(`🔍 Order details fetch successful on attempt ${attempt}`);

					return result;
				} catch (err: any) {
					console.log(`🔍 Attempt ${attempt} failed:`, err?.response?.status, err?.message);

					// Check if we should retry
					const shouldRetry =
						(err?.response?.status >= 500 || // Server errors
							err?.response?.status === 401) && // Auth errors
						attempt < maxRetries;

					if (!shouldRetry) {
						console.error(`Failed to fetch order detail after ${attempt} attempts:`, err);
						throw err;
					}

					// Wait before retrying
					console.log(`🔍 Retrying in ${retryDelay}ms...`);
					await new Promise((resolve) => setTimeout(resolve, retryDelay));
				}
			}
		},
		[getUpdateData, paymentReferenceFromHook, user?.session_id]
	);

	// Update orderDetail state when response changes
	useEffect(() => {
		const newOrderDetail = response
			? {
					...response,
					order_items: response?.order_items ?? null,
				}
			: null;

		if (!isEqual(orderDetail, newOrderDetail)) {
			setOrderDetail(newOrderDetail);
			if (newOrderDetail) {
				SET_ORDER_DETAIL(newOrderDetail);
				SET_ORDER_STATUS("success");
			}
		}
	}, [response, orderDetail, SET_ORDER_DETAIL, SET_ORDER_STATUS]);

	return {
		orderDetail,
		isLoading: isLoading || isValidating,
		error,
		refetch: getUpdateData,
		fetchOrderDetail,
	};
};

// Helper fetcher function to be used with mutate
const fetcher = async (url: string, config?: any) => {
	try {
		const response = await axios.get(url, config);

		return response.data;
	} catch (error: any) {
		generalRequestErrorHandler(error);
		throw error;
	}
};

export default useGetOrderDetail;
